import React, { useEffect, useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import moment from 'moment-timezone';
import { BarChart2 } from 'lucide-react';
import g50Currencies from '../../constants/g50Currencies';
import { useAppContext } from '../../App';
import { createFxProvider } from './providers/fxIntradayProvider.ts';
import { createEquityProvider } from './providers/equityIntradayProvider.ts';
import { MacroRegimeBadge } from './MacroRegimeBadge.tsx';
import { ConfluenceBadge } from './ConfluenceBadge.tsx';
import {
  computeConfluenceBadge,
} from './cftcRegimeUtils.ts';
import { useCftcRegimeSnapshot } from './useCftcRegimeSnapshot.ts';
import { runSetupEngine } from './engine/setupEngine.ts';
import { computeSessionBounds } from './engine/indicatorUtils.ts';
import type { Candle, DetectedSetup, LiquidityZone, SessionKey, Timeframe } from './engine/types.ts';
import { detectLiquidityZones } from '../Chart/LiquidityZone/detectLiquidityZones';
import styles from './TradeSetups.module.css';

const TIME_ZONE = 'America/New_York';
const fxProvider = createFxProvider();
const equityProvider = createEquityProvider();

type SetupHistoryItem = {
  id: string;
  setup: DetectedSetup;
  outcome: 'SUCCESS' | 'FAILED' | 'EXPIRED' | 'MIXED';
  resolvedAt: number | null;
};

const timeframeOptions: Array<{ label: string; value: Timeframe }> = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '1h', value: '1h' }
];

const sessionOptions: Array<{ label: string; value: SessionKey }> = [
  { label: 'FX (24h)', value: 'FX_24H' },
  { label: 'London (03:00–11:00 ET)', value: 'LONDON' },
  { label: 'New York (08:00–17:00 ET)', value: 'NEW_YORK' },
  { label: 'Custom…', value: 'CUSTOM' }
];

const formatEt = (timestamp: number) =>
  moment.tz(timestamp, TIME_ZONE).format('YYYY-MM-DD HH:mm');

const formatZoneTime = (time: string) =>
  moment.tz(time, 'YYYY-MM-DD HH:mm:ss', 'UTC').tz(TIME_ZONE).format('YYYY-MM-DD HH:mm');

const getZoneId = (zone: LiquidityZone) =>
  `${zone.startTime}-${zone.endTime}-${zone.proximal}-${zone.distal}`;

const getZoneRangeMs = (zone: LiquidityZone) => {
  const start = moment.tz(zone.startTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf();
  const end = moment.tz(zone.endTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf();
  return { start, end };
};

const defaultDateRange = () => {
  const end = moment.tz(TIME_ZONE);
  const start = end.clone().subtract(1, 'day');
  return {
    start: start.format('YYYY-MM-DDTHH:mm'),
    end: end.format('YYYY-MM-DDTHH:mm')
  };
};

const getTimeframeMinutes = (timeframe: Timeframe) => {
  switch (timeframe) {
    case '1m':
      return 1;
    case '5m':
      return 5;
    case '15m':
      return 15;
    case '1h':
    default:
      return 60;
  }
};

const getPriceDecimals = (symbol: string) => (symbol.includes('JPY') ? 3 : 5);

const isFxSymbol = (symbol: string) => symbol.includes('/');

const computeNextRefreshAt = (timeframe: Timeframe) => {
  const minutes = getTimeframeMinutes(timeframe);
  const now = moment.tz(TIME_ZONE);
  const remainder = now.minute() % minutes;
  let next = now.clone().add(minutes - remainder, 'minutes').startOf('minute');
  if (next.isSameOrBefore(now)) {
    next = next.add(minutes, 'minutes');
  }
  // Delay slightly after close so the FX service has the final candle.
  return next.valueOf() + 10000;
};

const getCurrentSessionLabel = () => {
  const hour = moment.utc().hour();
  if (hour >= 21 || hour < 7) {
    return 'Asia';
  }
  if (hour >= 7 && hour < 13) {
    return 'Europe';
  }
  return 'US';
};

const resolveSessionStartIndex = (
  candles: Candle[],
  session: SessionKey,
  sessionStart?: string,
  sessionEnd?: string
) => {
  if (!candles.length) return null;
  for (let i = candles.length - 1; i >= 0; i -= 1) {
    const bounds = computeSessionBounds(candles[i].t, session, sessionStart, sessionEnd);
    const time = moment.tz(candles[i].t, TIME_ZONE);
    if (time.isBetween(bounds.start, bounds.end, undefined, '[]')) {
      let firstIndex = i;
      for (let j = i; j >= 0; j -= 1) {
        const inSession = moment
          .tz(candles[j].t, TIME_ZONE)
          .isBetween(bounds.start, bounds.end, undefined, '[]');
        if (!inSession) {
          break;
        }
        firstIndex = j;
      }
      return firstIndex;
    }
  }
  return null;
};

const computeOrbRange = (
  candles: Candle[],
  session: SessionKey,
  timeframe: Timeframe,
  sessionStart?: string,
  sessionEnd?: string
) => {
  const startIndex = resolveSessionStartIndex(candles, session, sessionStart, sessionEnd);
  if (startIndex === null) return null;
  const orbWindowBars = Math.max(1, Math.round(30 / getTimeframeMinutes(timeframe)));
  const endIndex = Math.min(candles.length - 1, startIndex + orbWindowBars - 1);
  if (endIndex <= startIndex) return null;
  const slice = candles.slice(startIndex, endIndex + 1);
  const high = Math.max(...slice.map((c) => c.h));
  const low = Math.min(...slice.map((c) => c.l));
  return { startIndex, endIndex, high, low };
};

const buildCandlestickTrace = (candles: Candle[], symbol: string) => ({
  x: candles.map((c) => new Date(c.t)),
  open: candles.map((c) => c.o),
  high: candles.map((c) => c.h),
  low: candles.map((c) => c.l),
  close: candles.map((c) => c.c),
  type: 'candlestick',
  name: `${symbol} Candles`
});

const buildLineTrace = (candles: Candle[], values: Array<number | null>, name: string, color: string) => ({
  x: candles.map((c) => new Date(c.t)),
  y: values.map((v) => (v === null ? null : v)),
  type: 'scatter',
  mode: 'lines',
  name,
  line: { color, width: 1.5 }
});

const buildShapes = (
  candles: Candle[],
  setups: DetectedSetup[],
  range: { startIndex: number; endIndex: number; high: number; low: number } | null,
  orbRange: { startIndex: number; endIndex: number; high: number; low: number } | null,
  zoneOpacity: number,
  visibleZoneIds: Record<string, boolean>,
  showOrb: boolean,
  showLiquidityZones: boolean,
  liquidityZones: LiquidityZone[],
  liquidityZoneVisibility: Record<string, boolean>
) => {
  const shapes: any[] = [];
  const annotations: any[] = [];
  const xValues = candles.map((c) => new Date(c.t));
  const xStart = xValues[0];
  const xEnd = xValues[xValues.length - 1];
  const zoneCandles = 12;
  const clampedOpacity = Math.min(Math.max(zoneOpacity, 0.1), 0.8);
  const lineOpacity = Math.min(clampedOpacity + 0.05, 0.85);
  const renderLiquidity = showLiquidityZones || Object.values(liquidityZoneVisibility).some(Boolean);

  if (range) {
    shapes.push({
      type: 'rect',
      x0: xValues[range.startIndex],
      x1: xValues[range.endIndex],
      y0: range.low,
      y1: range.high,
      line: { color: '#8da0d8' },
      fillcolor: 'rgba(141,160,216,0.08)'
    });
  }

  if (orbRange && showOrb) {
    shapes.push({
      type: 'rect',
      x0: xValues[orbRange.startIndex],
      x1: xValues[orbRange.endIndex],
      y0: orbRange.low,
      y1: orbRange.high,
      line: { color: '#f6c453', dash: 'dot' },
      fillcolor: 'rgba(246,196,83,0.08)'
    });
    const midIndex = Math.floor((orbRange.startIndex + orbRange.endIndex) / 2);
    const rangeHeight = Math.max(orbRange.high - orbRange.low, Number.EPSILON);
    annotations.push({
      x: xValues[midIndex],
      y: orbRange.high + rangeHeight * 0.02,
      text: 'Opening Range',
      showarrow: false,
      font: { size: 11, color: '#f6c453' },
      xanchor: 'center',
      yanchor: 'bottom'
    });
  }

  if (renderLiquidity) {
    liquidityZones.forEach((zone) => {
      const id = getZoneId(zone);
      if (!liquidityZoneVisibility[id]) return;
      const zoneStartMs = moment.tz(zone.startTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf();
      const zoneEndMs = moment.tz(zone.endTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf();
      console.log('[TradeSetups] zone overlay', {
        id,
        zoneStart: zone.startTime,
        zoneEnd: zone.endTime,
        zoneStartMs,
        zoneEndMs,
        chartStart: xStart,
        chartEnd: xEnd,
        enabled: liquidityZoneVisibility[id]
      });
      if (!Number.isFinite(zoneStartMs) || !Number.isFinite(zoneEndMs)) return;
      const x0 = new Date(zoneStartMs);
      const x1 = new Date(zoneEndMs);
      const zoneX0 = x0 < xStart ? xStart : x0;
      const zoneX1 = x1 > xEnd ? xEnd : x1;
      if (zoneX1 <= zoneX0) return;
      const isDemand = (zone.type ?? '').toLowerCase() === 'demand';
      const lineColor = isDemand
        ? `rgba(46,204,113,${lineOpacity})`
        : `rgba(231,76,60,${lineOpacity})`;
      const fillColor = isDemand
        ? `rgba(46,204,113,${clampedOpacity})`
        : `rgba(231,76,60,${clampedOpacity})`;
      shapes.push({
        type: 'rect',
        x0: zoneX0,
        x1: zoneX1,
        y0: Math.min(zone.distal, zone.proximal),
        y1: Math.max(zone.distal, zone.proximal),
        layer: 'below',
        line: { color: lineColor, width: 1 },
        fillcolor: fillColor
      });
    });
  }

  setups.forEach((setup) => {
    if (visibleZoneIds[setup.id] === false) {
      return;
    }
    const patternStartAt = setup.patternStartAt ?? setup.detectedAt;
    const detectedIndex = candles.findIndex((c) => c.t >= patternStartAt);
    const zoneStartIndex = detectedIndex >= 0 ? detectedIndex : 0;
    const zoneStart = xValues[zoneStartIndex] ?? xStart;
    const zoneEndIndex = Math.min(xValues.length - 1, zoneStartIndex + zoneCandles);
    const zoneEnd = xValues[zoneEndIndex] ?? xEnd;
    const entryLevel = setup.entry.level;
    const stopLevel = setup.stop.level;
    const targetLevels = setup.targets.map((t) => t.level);

    if (targetLevels.length) {
      const targetLevel = targetLevels[0];
      const profitLow = Math.min(entryLevel, targetLevel);
      const profitHigh = Math.max(entryLevel, targetLevel);
      shapes.push({
        type: 'rect',
        x0: zoneStart,
        x1: zoneEnd,
        y0: profitLow,
        y1: profitHigh,
        layer: 'below',
        line: { color: `rgba(46,204,113,${lineOpacity})`, width: 0 },
        fillcolor: `rgba(46,204,113,${clampedOpacity})`
      });
    }

    if (Number.isFinite(stopLevel)) {
      const riskLow = Math.min(entryLevel, stopLevel);
      const riskHigh = Math.max(entryLevel, stopLevel);
      shapes.push({
        type: 'rect',
        x0: zoneStart,
        x1: zoneEnd,
        y0: riskLow,
        y1: riskHigh,
        layer: 'below',
        line: { color: `rgba(231,76,60,${lineOpacity})`, width: 0 },
        fillcolor: `rgba(231,76,60,${clampedOpacity})`
      });
    }

    if (showLiquidityZones && setup.liquidityZone) {
      const zone = setup.liquidityZone;
      const zoneStartMs = moment.tz(zone.startTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf();
      const zoneEndMs = moment.tz(zone.endTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf();
      if (Number.isFinite(zoneStartMs) && Number.isFinite(zoneEndMs)) {
        const x0 = new Date(zoneStartMs);
        const x1 = new Date(zoneEndMs);
        const zoneX0 = x0 < xStart ? xStart : x0;
        const zoneX1 = x1 > xEnd ? xEnd : x1;
        if (zoneX1 > zoneX0) {
          const isDemand = (zone.type ?? '').toLowerCase() === 'demand';
          const lineColor = isDemand
            ? `rgba(46,204,113,${lineOpacity})`
            : `rgba(231,76,60,${lineOpacity})`;
          const fillColor = isDemand
            ? `rgba(46,204,113,${clampedOpacity})`
            : `rgba(231,76,60,${clampedOpacity})`;
          shapes.push({
            type: 'rect',
            x0: zoneX0,
            x1: zoneX1,
            y0: Math.min(zone.distal, zone.proximal),
            y1: Math.max(zone.distal, zone.proximal),
            layer: 'below',
            line: { color: lineColor, width: 1 },
            fillcolor: fillColor
          });
        }
      }
    }

    setup.draw.forEach((draw) => {
      if (draw.kind === 'line') {
        shapes.push({
          type: 'line',
          x0: xStart,
          x1: xEnd,
          y0: draw.price,
          y1: draw.price,
          line: { color: draw.color || '#8da0d8', dash: 'dot', width: 1 }
        });
      }
      if (draw.kind === 'box' || draw.kind === 'zone') {
        const startIndex = draw.kind === 'box' ? draw.fromIndex : draw.startIndex;
        const endIndex = draw.kind === 'box' ? draw.toIndex : draw.endIndex;
        shapes.push({
          type: 'rect',
          x0: xValues[startIndex],
          x1: xValues[endIndex],
          y0: draw.low,
          y1: draw.high,
          line: { color: draw.color || '#8da0d8' },
          fillcolor: 'rgba(141,160,216,0.1)'
        });
      }
    });
  });

  return { shapes, annotations };
};

const confidenceBadgeClass = (confidence: DetectedSetup['confidence']) => {
  switch (confidence) {
    case 'HIGH':
      return styles.badgeHigh;
    case 'MED':
      return styles.badgeMed;
    default:
      return styles.badgeLow;
  }
};

const getSessionInfoForTimestamp = (timestamp: number, session: SessionKey) => {
  if (session === 'LONDON') {
    return { label: 'London', liquidity: 'High liquidity' };
  }
  if (session === 'NEW_YORK') {
    return { label: 'New York', liquidity: 'High liquidity' };
  }
  if (session === 'CUSTOM') {
    return { label: 'Custom', liquidity: '' };
  }
  const hour = moment.utc(timestamp).hour();
  if (hour >= 21 || hour < 7) {
    return { label: 'Asia', liquidity: 'Lower liquidity' };
  }
  if (hour >= 7 && hour < 13) {
    return { label: 'London', liquidity: 'High liquidity' };
  }
  return { label: 'New York', liquidity: 'High liquidity' };
};

const getConfidenceBullets = (setup: DetectedSetup) =>
  setup.checklist.map((item) => {
    if (item.pass) return item.label;
    if (item.key === 'volume') return 'VWAP computed without volume → confidence capped';
    return `${item.label} not met`;
  });

const parseLiquidityWindow = (zone: LiquidityZone) => ({
  start: moment.tz(zone.startTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf(),
  end: moment.tz(zone.endTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf()
});

const getLiquidityBadge = (setup: DetectedSetup, zones: LiquidityZone[]) => {
  if (setup.liquidityZone) {
    return { label: 'Liquidity Zone', tone: 'aligned', tooltip: 'Setup aligns with a nearby liquidity zone.' };
  }
  const counterZone = zones.find((zone) => {
    const window = parseLiquidityWindow(zone);
    const inWindow = setup.detectedAt >= window.start && setup.detectedAt <= window.end;
    if (!inWindow) return false;
    const type = (zone.type ?? '').toLowerCase();
    if (setup.direction === 'LONG') return type === 'supply';
    if (setup.direction === 'SHORT') return type === 'demand';
    return false;
  });
  if (counterZone) {
    return { label: 'Counter-Zone', tone: 'counter', tooltip: 'Setup is counter to a nearby liquidity zone.' };
  }
  return { label: 'No Zone', tone: 'none', tooltip: 'No nearby liquidity zone matched this setup.' };
};

const findCandleIndex = (candles: Candle[], timestamp: number) => {
  const idx = candles.findIndex((c) => c.t >= timestamp);
  if (idx >= 0) return idx;
  return candles.length ? candles.length - 1 : -1;
};

const computeDisplayStatus = (params: {
  setup: DetectedSetup;
  candles: Candle[];
  indicators: { ema9: Array<number | null>; ema20: Array<number | null> };
  range: { high: number; low: number } | null;
  timeframe: Timeframe;
  session: SessionKey;
  sessionStart?: string;
  sessionEnd?: string;
}): { status: DetectedSetup['status']; barsAgo: number | null; expiredReason: string } => {
  // Visual-only lifecycle overlay; does not alter detection logic.
  const { setup, candles, indicators, range, timeframe, session, sessionStart, sessionEnd } = params;
  const lastIndex = candles.length - 1;
  if (lastIndex < 0) {
    return { status: setup.status, barsAgo: null, expiredReason: '' };
  }
  const detectedIndex = findCandleIndex(candles, setup.detectedAt);
  const barsSinceDetected = Math.max(lastIndex - detectedIndex, 0);
  if (setup.status === 'TRIGGERED') {
    return { status: 'TRIGGERED', barsAgo: barsSinceDetected, expiredReason: '' };
  }
  if (setup.status === 'ACTIVE') {
    let expiredAtIndex: number | null = null;
    let expiredReason = '';
    if (['EMA_PULLBACK', 'FLAG', 'FAILED_BREAKOUT'].includes(setup.setup)) {
      if (barsSinceDetected >= 20) {
        expiredAtIndex = detectedIndex + 20;
        expiredReason = 'Time window elapsed';
      }
    } else if (setup.setup === 'ORB') {
      const barsFor3h = Math.ceil(180 / getTimeframeMinutes(timeframe));
      if (barsSinceDetected >= barsFor3h) {
        expiredAtIndex = detectedIndex + barsFor3h;
        expiredReason = 'ORB window elapsed';
      } else {
        const bounds = computeSessionBounds(setup.detectedAt, session, sessionStart, sessionEnd);
        if (candles[lastIndex].t > bounds.end.valueOf()) {
          expiredAtIndex = lastIndex;
          expiredReason = 'Session ended';
        }
      }
    } else if (setup.setup === 'RANGE_FADE') {
      if (range && lastIndex >= 1) {
        const prevClose = candles[lastIndex - 1].c;
        const lastClose = candles[lastIndex].c;
        if ((prevClose > range.high && lastClose > range.high) || (prevClose < range.low && lastClose < range.low)) {
          expiredAtIndex = lastIndex;
          expiredReason = 'Accepted outside range';
        }
      }
    } else if (setup.setup === 'EXHAUSTION_REVERSAL') {
      if (barsSinceDetected >= 30) {
        expiredAtIndex = detectedIndex + 30;
        expiredReason = 'Window elapsed';
      } else {
        const ema9 = indicators.ema9[lastIndex];
        const ema20 = indicators.ema20[lastIndex];
        const close = candles[lastIndex].c;
        if (ema9 && ema20) {
          if (setup.direction === 'LONG' && ema9 < ema20 && close < ema20) {
            expiredAtIndex = lastIndex;
            expiredReason = 'Trend reasserted lower';
          }
          if (setup.direction === 'SHORT' && ema9 > ema20 && close > ema20) {
            expiredAtIndex = lastIndex;
            expiredReason = 'Trend reasserted higher';
          }
        }
      }
    }
    if (expiredAtIndex !== null) {
      return { status: 'EXPIRED', barsAgo: Math.max(lastIndex - expiredAtIndex, 0), expiredReason };
    }
  }
  return { status: setup.status, barsAgo: null, expiredReason: '' };
};

const findTriggerIndexByLevel = (setup: DetectedSetup, candles: Candle[]) => {
  if (!candles.length) return null;
  const entryLevel = setup.entry?.level;
  if (entryLevel == null) return null;
  const startIndex = findCandleIndex(candles, setup.detectedAt);
  if (startIndex < 0) return null;
  for (let i = startIndex; i < candles.length; i += 1) {
    const candle = candles[i];
    if (setup.direction === 'LONG') {
      if (candle.h >= entryLevel) return i;
    } else if (candle.l <= entryLevel) {
      return i;
    }
  }
  return null;
};

const evaluateSetupOutcome = (
  setup: DetectedSetup,
  candles: Candle[]
): { outcome: SetupHistoryItem['outcome'] | null; resolvedAt: number | null } => {
  const stopLevel = setup.stop?.level;
  const targetLevel = setup.targets?.[0]?.level;
  if (stopLevel == null || targetLevel == null) {
    return { outcome: null, resolvedAt: null };
  }
  const triggerIndex = findTriggerIndexByLevel(setup, candles);
  if (triggerIndex == null) {
    return { outcome: null, resolvedAt: null };
  }
  for (let i = triggerIndex; i < candles.length; i += 1) {
    const candle = candles[i];
    const hitTarget =
      setup.direction === 'LONG' ? candle.h >= targetLevel : candle.l <= targetLevel;
    const hitStop = setup.direction === 'LONG' ? candle.l <= stopLevel : candle.h >= stopLevel;
    if (hitTarget && hitStop) {
      return { outcome: 'MIXED', resolvedAt: candle.t };
    }
    if (hitTarget) {
      return { outcome: 'SUCCESS', resolvedAt: candle.t };
    }
    if (hitStop) {
      return { outcome: 'FAILED', resolvedAt: candle.t };
    }
  }
  return { outcome: null, resolvedAt: null };
};

const statusBadgeClass = (status: DetectedSetup['status']) => {
  switch (status) {
    case 'TRIGGERED':
      return styles.badgeTriggered;
    case 'ACTIVE':
      return styles.badgeActive;
    default:
      return styles.badgeExpired;
  }
};

const setupLabel = (setup: DetectedSetup) => {
  switch (setup.setup) {
    case 'EMA_PULLBACK':
      return 'EMA Pullback';
    case 'FLAG':
      return 'Flag / Pennant';
    case 'VWAP_RECLAIM':
      return 'VWAP Reclaim';
    case 'RANGE_FADE':
      return 'Range Fade';
    case 'FAILED_BREAKOUT':
      return 'Failed Breakout';
    case 'ORB':
      return 'Opening Range Breakout';
    case 'EXHAUSTION_REVERSAL':
      return 'Momentum Reversal';
    default:
      return setup.setup;
  }
};

const getPatternStartAt = (setup: DetectedSetup) => setup.patternStartAt ?? setup.detectedAt;

const setupEducation: Record<
  DetectedSetup['setup'],
  { title: string; bullets: string[] }
> = {
  EMA_PULLBACK: {
    title: 'EMA Pullback (trend continuation)',
    bullets: [
      'Trend holds (EMA9 above/below EMA20 with structure).',
      'Pullback tags EMA9/EMA20 without breaking trend.',
      'Trigger on close back through EMA9.',
      'Stop = pullback swing; target = prior swing / 2–3R.'
    ]
  },
  FLAG: {
    title: 'Flag / Pennant',
    bullets: [
      'Sharp impulse move followed by tight consolidation.',
      'Volatility contracts and candles overlap.',
      'Trigger on breakout of flag boundary.',
      'Target = measured move from impulse.'
    ]
  },
  VWAP_RECLAIM: {
    title: 'VWAP Reclaim',
    bullets: [
      'Price reclaims session VWAP and holds.',
      'Trigger on retest/bounce or continuation close.',
      'Stop = close back below VWAP.',
      'Target = session high/low or 1–2R.'
    ]
  },
  RANGE_FADE: {
    title: 'Range High/Low Fade',
    bullets: [
      'Range detected via compression and stable highs/lows.',
      'Rejection wick beyond range with close back inside.',
      'Trigger on rejection confirmation.',
      'Target = midpoint then opposite range edge.'
    ]
  },
  FAILED_BREAKOUT: {
    title: 'Failed Breakout (trap)',
    bullets: [
      'Price breaks a key level then snaps back inside.',
      'Failure confirmed within 1–3 candles.',
      'Trigger on retest of failed level.',
      'Stop beyond failed high/low; target toward range.'
    ]
  },
  ORB: {
    title: 'Opening Range Breakout',
    bullets: [
      'Define first 30-minute range for the session.',
      'Trigger on close outside OR with follow-through.',
      'Stop back inside OR or opposite boundary.',
      'Target = OR height or 1.5–3R.'
    ]
  },
  EXHAUSTION_REVERSAL: {
    title: 'Momentum Reversal (exhaustion)',
    bullets: [
      'Extension far from EMA20 / large range candle.',
      'Confirmation via minor structure break.',
      'Trigger on confirmation close.',
      'Target = VWAP/EMA20 mean reversion.'
    ]
  }
};

const getSetupTooltip = (setup: DetectedSetup) =>
  setupEducation[setup.setup] || {
    title: setup.setup,
    bullets: ['Pattern setup description unavailable.']
  };

export default function TradeSetups() {
  const { theme } = useAppContext();
  const axisColor = theme === 'light' ? '#1b1f2a' : '#ffffff';
  const [symbol, setSymbol] = useState('EUR/USD');
  const priceDecimals = getPriceDecimals(symbol);
  const priceTickFormat = priceDecimals === 3 ? '.3f' : '.5f';
  const [customSymbols, setCustomSymbols] = useState<string[]>(() => {
    try {
      const stored = window.localStorage.getItem('tradeSetups.customSymbols');
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  });
  const [newSymbol, setNewSymbol] = useState('');
  const [symbolError, setSymbolError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<Timeframe>('5m');
  const [session, setSession] = useState<SessionKey>('FX_24H');
  const [strictMode, setStrictMode] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [setupsTab, setSetupsTab] = useState<'current' | 'history' | 'liquidity'>('current');
  const [showHistoryStats, setShowHistoryStats] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const historyPageSize = 6;
  const [expandedHistoryIds, setExpandedHistoryIds] = useState<Record<string, boolean>>({});
  const [qualityFilters, setQualityFilters] = useState({
    minRR: 'any',
    session: 'ALL',
    macro: 'ANY',
    liquidity: 'ANY',
    requireVWAP: false
  });
  const [customSessionStart, setCustomSessionStart] = useState('09:00');
  const [customSessionEnd, setCustomSessionEnd] = useState('12:00');
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [autoEnd, setAutoEnd] = useState(true);
  const [candles, setCandles] = useState<Candle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoneOpacity, setZoneOpacity] = useState(() => {
    const saved = window.localStorage.getItem('tradeSetups.zoneOpacity');
    const parsed = saved ? Number.parseFloat(saved) : 0.4;
    return Number.isFinite(parsed) ? parsed : 0.4;
  });
  const [showLiquidityZones, setShowLiquidityZones] = useState(() => {
    const stored = window.localStorage.getItem('tradeSetups.showLiquidityZones');
    if (stored == null) return true;
    return stored === 'true';
  });
  const [liquidityPersistence, setLiquidityPersistence] = useState(() => {
    const stored = window.localStorage.getItem('tradeSetups.liquidityPersistence');
    const parsed = stored ? Number.parseInt(stored, 10) : 3;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 3;
  });
  const [liquidityStdDev, setLiquidityStdDev] = useState(() => {
    const stored = window.localStorage.getItem('tradeSetups.liquidityStdDev');
    const parsed = stored ? Number.parseFloat(stored) : 1.25;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1.25;
  });
  const [showZoneMenu, setShowZoneMenu] = useState(false);
  const [visibleZoneIds, setVisibleZoneIds] = useState<Record<string, boolean>>({});
  const [historyVisibleIds, setHistoryVisibleIds] = useState<Record<string, boolean>>({});
  const [liquidityZoneVisibility, setLiquidityZoneVisibility] = useState<Record<string, boolean>>({});
  const [chartRangeOverride, setChartRangeOverride] = useState<{ start: number; end: number } | null>(null);
  const [chartYOverride, setChartYOverride] = useState<[number, number] | null>(null);
  const [fitSetupId, setFitSetupId] = useState<string | null>(null);
  const [fitEnabled, setFitEnabled] = useState(true);
  const [timeZoomSetupId, setTimeZoomSetupId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [liquidityZones, setLiquidityZones] = useState<LiquidityZone[]>([]);
  const [nextRefreshAt, setNextRefreshAt] = useState<number | null>(null);
  const [refreshCountdown, setRefreshCountdown] = useState('');
  const [sessionLabel, setSessionLabel] = useState(getCurrentSessionLabel);
  const [sessionTime, setSessionTime] = useState(moment.utc().format('HH:mm'));
  const { snapshot: macroSnapshot, stale: macroStale } = useCftcRegimeSnapshot(symbol);
  const historyKey = useMemo(() => `tradeSetups.history.${symbol}.${timeframe}`, [symbol, timeframe]);
  const [setupHistory, setSetupHistory] = useState<SetupHistoryItem[]>([]);

  const symbolOptions = useMemo(() => {
    const fxSymbols = g50Currencies.map((item) => item.pair);
    const all = [...fxSymbols, ...customSymbols];
    return Array.from(new Set(all));
  }, [customSymbols]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(historyKey);
      const parsed = stored ? JSON.parse(stored) : [];
      setSetupHistory(Array.isArray(parsed) ? parsed : []);
    } catch (err) {
      setSetupHistory([]);
    }
  }, [historyKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem('tradeSetups.qualityFilters.v1');
      if (stored) {
        const parsed = JSON.parse(stored);
        setQualityFilters((prev) => ({ ...prev, ...parsed }));
      }
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('tradeSetups.qualityFilters.v1', JSON.stringify(qualityFilters));
    } catch (err) {
      // ignore
    }
  }, [qualityFilters]);

  useEffect(() => {
    try {
      window.localStorage.setItem(historyKey, JSON.stringify(setupHistory));
    } catch (err) {
      // Ignore storage failures (private mode, quota).
    }
  }, [historyKey, setupHistory]);

  useEffect(() => {
    let timeoutId = null;
    const scheduleNext = () => {
      const nextAt = computeNextRefreshAt(timeframe);
      setNextRefreshAt(nextAt);
      const delayMs = nextAt - Date.now();
      timeoutId = setTimeout(() => {
        setRefreshTick((prev) => prev + 1);
      }, Math.max(delayMs, 1000));
    };
    scheduleNext();
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeframe, symbol, dateRange.start, dateRange.end, refreshTick]);

  useEffect(() => {
    if (!nextRefreshAt) {
      setRefreshCountdown('');
      return;
    }
    const intervalId = setInterval(() => {
      const diff = nextRefreshAt - Date.now();
      if (diff <= 0) {
        setRefreshCountdown('Updating...');
        return;
      }
      const totalSeconds = Math.ceil(diff / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      setRefreshCountdown(minutes + ':' + seconds.toString().padStart(2, '0'));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [nextRefreshAt]);

  useEffect(() => {
    const tick = () => {
      setSessionLabel(getCurrentSessionLabel());
      setSessionTime(moment.utc().format('HH:mm'));
    };
    tick();
    const intervalId = setInterval(tick, 60000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!autoEnd) return;
    const now = moment.tz(TIME_ZONE);
    setDateRange((prev) => {
      const prevStart = moment.tz(prev.start, TIME_ZONE);
      const prevEnd = moment.tz(prev.end, TIME_ZONE);
      const durationMs = Math.max(prevEnd.diff(prevStart), 0);
      const nextEnd = now;
      const nextStart = durationMs
        ? now.clone().subtract(durationMs, 'milliseconds')
        : now.clone().subtract(1, 'day');
      const nextRange = {
        start: nextStart.format('YYYY-MM-DDTHH:mm'),
        end: nextEnd.format('YYYY-MM-DDTHH:mm')
      };
      if (nextRange.start === prev.start && nextRange.end === prev.end) {
        return prev;
      }
      return nextRange;
    });
  }, [refreshTick, autoEnd]);

  useEffect(() => {
    window.localStorage.setItem('tradeSetups.zoneOpacity', zoneOpacity.toFixed(2));
  }, [zoneOpacity]);

  useEffect(() => {
    window.localStorage.setItem('tradeSetups.showLiquidityZones', String(showLiquidityZones));
  }, [showLiquidityZones]);

  useEffect(() => {
    window.localStorage.setItem('tradeSetups.liquidityPersistence', String(liquidityPersistence));
  }, [liquidityPersistence]);

  useEffect(() => {
    window.localStorage.setItem('tradeSetups.liquidityStdDev', liquidityStdDev.toFixed(2));
  }, [liquidityStdDev]);

  useEffect(() => {
    if (!showLiquidityZones && setupsTab === 'liquidity') {
      setSetupsTab('current');
    }
  }, [showLiquidityZones, setupsTab]);

  useEffect(() => {
    if (!chartRangeOverride) {
      setTimeZoomSetupId(null);
    }
  }, [chartRangeOverride]);

  useEffect(() => {
    window.localStorage.setItem('tradeSetups.customSymbols', JSON.stringify(customSymbols));
  }, [customSymbols]);

  useEffect(() => {
    let isMounted = true;
    const fetchCandles = async () => {
      setLoading(true);
      setError(null);
      try {
        const provider = isFxSymbol(symbol) ? fxProvider : equityProvider;
        const data = await provider.fetchCandles({
          symbol,
          timeframe,
          start: dateRange.start,
          end: dateRange.end
        });
        const sorted = [...data].sort((a, b) => a.t - b.t);
        if (isMounted) {
          setCandles(sorted);
        }
      } catch (err: any) {
        if (isMounted) {
          const message = err?.message || 'Failed to load intraday data.';
          setError(message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchCandles();
    return () => {
      isMounted = false;
    };
  }, [symbol, timeframe, dateRange.start, dateRange.end, refreshTick]);

  useEffect(() => {
    if (!candles.length) {
      setLiquidityZones([]);
      setLiquidityZoneVisibility({});
      return;
    }
    const normalized = candles
      .map((c) => ({
        time: moment.tz(c.t, 'UTC').format('YYYY-MM-DD HH:mm:ss'),
        open: c.o,
        high: c.h,
        low: c.l,
        close: c.c,
        volume: c.v ?? 0
      }))
      .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
    const { liquidityZones: zones } = detectLiquidityZones(
      normalized,
      liquidityPersistence,
      liquidityStdDev
    );
    const normalizedZones = (Array.isArray(zones) ? zones : []).map((zone) => {
      const start = moment.tz(zone.startTime, 'YYYY-MM-DD HH:mm:ss', 'UTC');
      const end = moment.tz(zone.endTime, 'YYYY-MM-DD HH:mm:ss', 'UTC');
      if (end.isBefore(start)) {
        return { ...zone, startTime: zone.endTime, endTime: zone.startTime };
      }
      return zone;
    });
    setLiquidityZones(normalizedZones);
    setLiquidityZoneVisibility((prev) => {
      const next = { ...prev };
      normalizedZones.forEach((zone) => {
        const id = getZoneId(zone);
        if (next[id] == null) next[id] = false;
      });
      Object.keys(next).forEach((id) => {
        if (!normalizedZones.some((zone) => getZoneId(zone) == id)) {
          delete next[id];
        }
      });
      return next;
    });
  }, [candles, liquidityPersistence, liquidityStdDev]);

  const engineOutput = useMemo(() => {
    if (!candles.length) {
      return null;
    }
    return runSetupEngine({
      symbol,
      timeframe,
      session,
      sessionStart: session === 'CUSTOM' ? customSessionStart : undefined,
      sessionEnd: session === 'CUSTOM' ? customSessionEnd : undefined,
      strictMode,
      onlyActive: false,
      candles,
      liquidityZones
    });
  }, [candles, symbol, timeframe, session, customSessionStart, customSessionEnd, strictMode, liquidityZones]);

  const setupDisplays = useMemo(() => {
    if (!engineOutput) return [];
    return engineOutput.setups.map((setup) => ({
      setup,
      display: computeDisplayStatus({
        setup,
        candles,
        indicators: engineOutput.indicators || { ema9: [], ema20: [] },
        range: engineOutput.range || null,
        timeframe,
        session,
        sessionStart: session === 'CUSTOM' ? customSessionStart : undefined,
        sessionEnd: session === 'CUSTOM' ? customSessionEnd : undefined
      })
    }));
  }, [engineOutput, candles, timeframe, session, customSessionStart, customSessionEnd]);

  const filteredSetups = useMemo(() => {
    const minRRValue = qualityFilters.minRR === 'any' ? null : Number(qualityFilters.minRR);
    const requiresVWAP = qualityFilters.requireVWAP;
    const sessionFilter = qualityFilters.session;
    const macroFilter = qualityFilters.macro;
    const liquidityFilter = qualityFilters.liquidity;
    return setupDisplays.filter(({ setup, display }) => {
      if (onlyActive && display.status !== 'ACTIVE') return false;
      if (minRRValue != null) {
        const entry = setup.entry?.level;
        const stop = setup.stop?.level;
        const target = setup.targets?.[0]?.level;
        if (entry == null || stop == null || target == null) return false;
        const risk = Math.abs(entry - stop);
        const reward = Math.abs(target - entry);
        if (!risk || reward / risk < minRRValue) return false;
      }
      if (requiresVWAP) {
        const hasVwap =
          setup.setup === 'VWAP_RECLAIM' ||
          setup.why?.some((reason) => reason.toLowerCase().includes('vwap'));
        if (!hasVwap) return false;
      }
      if (sessionFilter !== 'ALL') {
        const label = getSessionInfoForTimestamp(getPatternStartAt(setup), session).label;
        if (label.toUpperCase() !== sessionFilter) return false;
      }
      if (macroFilter === 'ALIGNED') {
        const confluence = computeConfluenceBadge(
          setup.direction,
          macroSnapshot?.macroBias ?? null,
          Boolean(macroSnapshot)
        );
        if (confluence.tone !== 'aligned') return false;
      }
      if (liquidityFilter === 'ALIGNED' && !setup.liquidityZone) return false;
      return true;
    });
  }, [setupDisplays, onlyActive, qualityFilters, session, macroSnapshot]);

  const getFitRangeForSetup = (setup: DetectedSetup) => {
    const levels = [
      setup.entry?.level,
      setup.stop?.level,
      ...setup.targets.map((t) => t.level)
    ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    if (!levels.length) return null;
    const minLevel = Math.min(...levels);
    const maxLevel = Math.max(...levels);
    const span = Math.max(maxLevel - minLevel, Number.EPSILON);
    const pad = span * 0.1;
    return [minLevel - pad, maxLevel + pad] as [number, number];
  };

  useEffect(() => {
    if (!filteredSetups.length) {
      setFitSetupId(null);
      setChartYOverride(null);
      return;
    }
    const first = filteredSetups[0].setup;
    const sameRange = (next: [number, number] | null) =>
      next &&
      chartYOverride &&
      Math.abs(next[0] - chartYOverride[0]) < 1e-9 &&
      Math.abs(next[1] - chartYOverride[1]) < 1e-9;

    if (!fitEnabled) {
      return;
    }
    if (!fitSetupId) {
      const range = getFitRangeForSetup(first);
      if (range) {
        if (!sameRange(range)) setChartYOverride(range);
        setFitSetupId(first.id);
      }
      return;
    }
    const active = filteredSetups.find(({ setup }) => setup.id === fitSetupId);
    if (active) {
      const range = getFitRangeForSetup(active.setup);
      if (range) {
        if (!sameRange(range)) setChartYOverride(range);
      }
      return;
    }
    const range = getFitRangeForSetup(first);
    if (range) {
      if (!sameRange(range)) setChartYOverride(range);
      setFitSetupId(first.id);
    } else {
      setFitSetupId(null);
      setChartYOverride(null);
    }
  }, [filteredSetups, fitSetupId, chartYOverride, fitEnabled]);

  useEffect(() => {
    if (!engineOutput) return;
    setVisibleZoneIds((prev) => {
      const next = { ...prev };
      setupDisplays.forEach(({ setup }) => {
        if (next[setup.id] === undefined) {
          next[setup.id] = true;
        }
      });
      Object.keys(next).forEach((id) => {
        if (!setupDisplays.some(({ setup }) => setup.id === id)) {
          delete next[id];
        }
      });
      return next;
    });
  }, [engineOutput, setupDisplays]);

  const historyItems = setupHistory;
  const totalHistoryPages = Math.max(1, Math.ceil(historyItems.length / historyPageSize));
  const pagedHistoryItems = useMemo(() => {
    const start = (historyPage - 1) * historyPageSize;
    return historyItems.slice(start, start + historyPageSize);
  }, [historyItems, historyPage, historyPageSize]);
  const historyStats = useMemo(() => {
    const total = historyItems.length;
    const success = historyItems.filter((item) => item.outcome === 'SUCCESS').length;
    const failed = historyItems.filter((item) => item.outcome === 'FAILED').length;
    const expired = historyItems.filter((item) => item.outcome === 'EXPIRED').length;
    const mixed = historyItems.filter((item) => item.outcome === 'MIXED').length;
    const other = expired + mixed;
    const pct = (count: number) => (total ? ((count / total) * 100).toFixed(1) : '0.0');
    return {
      total,
      success,
      failed,
      expired,
      mixed,
      other,
      successPct: pct(success),
      failedPct: pct(failed),
      expiredPct: pct(expired),
      mixedPct: pct(mixed),
      otherPct: pct(other)
    };
  }, [historyItems]);

  const [historyPieColors, setHistoryPieColors] = useState<string[]>([
    '#22c55e',
    '#ef4444',
    '#f59e0b'
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!showHistoryStats) return;
    const root = window.getComputedStyle(document.documentElement);
    const resolve = (name: string, fallback: string) =>
      root.getPropertyValue(name).trim() || fallback;
    setHistoryPieColors([
      resolve('--color-success', '#22c55e'),
      resolve('--color-danger', '#ef4444'),
      resolve('--color-warning', '#f59e0b')
    ]);
  }, [showHistoryStats]);

  const historyPieData = useMemo(
    () => [
      {
        type: 'pie' as const,
        values: [historyStats.success, historyStats.failed, historyStats.other],
        labels: ['Success', 'Failed', 'Other'],
        hole: 0.45,
        textinfo: 'percent',
        textposition: 'inside',
        textfont: {
          size: 18,
          color: '#000000',
          family: 'Arial Black, Arial, sans-serif'
        },
        hoverlabel: {
          bgcolor: '#ffffff',
          bordercolor: '#000000',
          font: {
            color: '#000000',
            size: 18,
            family: 'Arial Black, Arial, sans-serif'
          }
        },
        marker: { colors: historyPieColors },
        hoverinfo: 'label+percent'
      }
    ],
    [historyStats, historyPieColors]
  );

  const historyPieLayout = useMemo(
    () => ({
      margin: { t: 0, b: 0, l: 0, r: 0 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      showlegend: false,
      autosize: true
    }),
    []
  );

  const historySetupsForChart = useMemo(
    () => historyItems.filter((item) => historyVisibleIds[item.id]).map((item) => item.setup),
    [historyItems, historyVisibleIds]
  );

  const chartData = useMemo(() => {
    if (!engineOutput) return null;
    const { indicators, setups, range } = engineOutput;
    const baseSetupsForChart = onlyActive
      ? filteredSetups.map(({ setup }) => setup)
      : setups;
    const setupsForChart = [
      ...baseSetupsForChart,
      ...historySetupsForChart.filter(
        (historySetup) => !baseSetupsForChart.some((setup) => setup.id === historySetup.id)
      )
    ];
    const orbRange = computeOrbRange(
      candles,
      session,
      timeframe,
      session === 'CUSTOM' ? customSessionStart : undefined,
      session === 'CUSTOM' ? customSessionEnd : undefined
    );
    const traces = [
      buildCandlestickTrace(candles, symbol),
      buildLineTrace(candles, indicators.ema9, 'EMA 9', '#8da0d8'),
      buildLineTrace(candles, indicators.ema20, 'EMA 20', '#f6c453'),
      buildLineTrace(candles, indicators.vwap, indicators.vwapLabel, '#00cc00')
    ];
    const hasOrb = setupsForChart.some((setup) => setup.setup === 'ORB');
    const combinedVisibleZoneIds = {
      ...visibleZoneIds,
      ...Object.keys(historyVisibleIds).reduce((acc, id) => {
        if (historyVisibleIds[id]) acc[id] = true;
        return acc;
      }, {} as Record<string, boolean>)
    };
    const { shapes, annotations } = buildShapes(
      candles,
      setupsForChart,
      range,
      orbRange,
      zoneOpacity,
      combinedVisibleZoneIds,
      hasOrb,
      showLiquidityZones,
      liquidityZones,
      liquidityZoneVisibility
    );

    const rangeStart = chartRangeOverride?.start ?? candles[0]?.t ?? 0;
    const rangeEnd = chartRangeOverride?.end ?? candles[candles.length - 1]?.t ?? 0;
    const visibleCandles = candles.filter((c) => c.t >= rangeStart && c.t <= rangeEnd);
    const lows = visibleCandles.map((c) => c.l);
    const highs = visibleCandles.map((c) => c.h);
    const minLow = lows.length ? Math.min(...lows) : null;
    const maxHigh = highs.length ? Math.max(...highs) : null;
    let yRange = null;
    if (minLow != null && maxHigh != null) {
      const span = Math.max(maxHigh - minLow, Number.EPSILON);
      const pad = span * 0.05;
      yRange = [minLow - pad, maxHigh + pad];
    }
    const finalYRange = chartYOverride ?? yRange;
    return { traces, shapes, annotations, yRange: finalYRange };
  }, [
    engineOutput,
    candles,
    symbol,
    session,
    timeframe,
    customSessionStart,
    customSessionEnd,
    onlyActive,
    filteredSetups,
    historySetupsForChart,
    historyVisibleIds,
    zoneOpacity,
    visibleZoneIds,
    showLiquidityZones,
    liquidityZones,
    liquidityZoneVisibility,
    chartRangeOverride,
    chartYOverride
  ]);

  useEffect(() => {
    if (!setupDisplays.length) return;
    setSetupHistory((prev) => {
      const next = [...prev];
      setupDisplays.forEach(({ setup, display }) => {
        const existingIndex = next.findIndex((item) => item.id === setup.id);
        const outcomeResult = evaluateSetupOutcome(setup, candles);
        const resolvedOutcome =
          outcomeResult.outcome ??
          (display.status === 'EXPIRED' ? 'EXPIRED' : null);
        if (!resolvedOutcome) return;
        const resolvedAt = outcomeResult.resolvedAt ?? (display.status === 'EXPIRED' ? candles.at(-1)?.t ?? null : null);
        if (existingIndex >= 0) {
          if (next[existingIndex].outcome !== resolvedOutcome) {
            next[existingIndex] = { ...next[existingIndex], outcome: resolvedOutcome, resolvedAt };
          }
        } else {
          next.unshift({ id: setup.id, setup, outcome: resolvedOutcome, resolvedAt });
        }
      });
      return next;
    });
  }, [setupDisplays, candles]);

  const setups = filteredSetups.map(({ setup }) => setup);

  useEffect(() => {
    if (!historyItems.length) return;
    setHistoryVisibleIds((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!historyItems.some((item) => item.id === id)) {
          delete next[id];
        }
      });
      return next;
    });
  }, [historyItems]);

  useEffect(() => {
    if (historyPage > totalHistoryPages) {
      setHistoryPage(totalHistoryPages);
    }
  }, [historyPage, totalHistoryPages]);

  return (
    <div className={`${styles.container} ${showZoneMenu ? styles.menuOpen : ''}`}>
      <div className={styles.banner}>
        <span className={styles.bannerText}>
          Pattern scanner for education and decision support. Not financial advice.
        </span>
        <div className={styles.bannerMenu}>
          <button
            type="button"
            className={styles.kebabButton}
            onClick={() => setShowZoneMenu((prev) => !prev)}
            aria-label="Zone transparency"
          >
            ...
          </button>
          {showZoneMenu && (
            <>
              <div
                className={styles.kebabBackdrop}
                onClick={() => setShowZoneMenu(false)}
                aria-hidden="true"
              />
              <div className={styles.kebabMenu}>
                <div className={styles.menuLabel}>Zone transparency</div>
                <input
                  className={styles.slider}
                  type="range"
                  min="0.1"
                  max="0.6"
                  step="0.05"
                  value={zoneOpacity}
                  onChange={(e) => setZoneOpacity(parseFloat(e.target.value))}
                />
                <div className={styles.menuValue}>{Math.round(zoneOpacity * 100)}%</div>
                <label className={styles.menuToggle}>
                  <input
                    type="checkbox"
                    checked={showLiquidityZones}
                    onChange={() => setShowLiquidityZones((prev) => !prev)}
                  />
                  {' '}Show liquidity zones
                </label>
                <div className={styles.liquiditySettingsBox}>
                  <div className={styles.liquiditySettingsTitle}>Liquidity zone settings</div>
                  <div className={styles.menuField}>
                    <span className={styles.controlLabel}>Zone width (std dev)</span>
                    <input
                      className={styles.slider}
                      type="range"
                      min="0.5"
                      max="2.5"
                      step="0.05"
                      value={liquidityStdDev}
                      onChange={(e) => setLiquidityStdDev(parseFloat(e.target.value))}
                    />
                    <div className={styles.menuValue}>{liquidityStdDev.toFixed(2)}×</div>
                  </div>
                  <div className={styles.menuField}>
                    <span className={styles.controlLabel}>Persistence</span>
                    <select
                      className={styles.select}
                      value={String(liquidityPersistence)}
                      onChange={(e) => setLiquidityPersistence(parseInt(e.target.value, 10))}
                    >
                      {[1, 2, 3, 4, 5].map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className={styles.menuDivider} />
                <div className={styles.menuSection}>
                  <div className={styles.menuLabelRow}>
                    <span className={styles.menuLabel}>Quality filters</span>
                    <span className={styles.menuInfoIcon} tabIndex={0}>
                      ?
                      <span className={styles.menuTooltip}>
                        Filters refine the current list and chart overlays without changing detection logic or history.
                      </span>
                    </span>
                  </div>
                  <div className={styles.menuField}>
                    <span className={styles.controlLabel}>Min R:R</span>
                    <select
                      className={styles.select}
                      value={qualityFilters.minRR}
                      onChange={(e) => setQualityFilters((prev) => ({ ...prev, minRR: e.target.value }))}
                    >
                      <option value="any">Any</option>
                      <option value="1.5">1.5+</option>
                      <option value="2">2+</option>
                      <option value="2.5">2.5+</option>
                      <option value="3">3+</option>
                    </select>
                  </div>
                  <div className={styles.menuField}>
                    <span className={styles.controlLabel}>Session</span>
                    <select
                      className={styles.select}
                      value={qualityFilters.session}
                      onChange={(e) => setQualityFilters((prev) => ({ ...prev, session: e.target.value }))}
                    >
                      <option value="ALL">All</option>
                      <option value="ASIA">Asia</option>
                      <option value="LONDON">London</option>
                      <option value="NEW YORK">New York</option>
                    </select>
                  </div>
                  <div className={styles.menuField}>
                    <span className={styles.controlLabel}>Macro alignment</span>
                    <select
                      className={styles.select}
                      value={qualityFilters.macro}
                      onChange={(e) => setQualityFilters((prev) => ({ ...prev, macro: e.target.value }))}
                    >
                      <option value="ANY">Any</option>
                      <option value="ALIGNED">Aligned only</option>
                    </select>
                  </div>
                  <div className={styles.menuField}>
                    <span className={styles.controlLabel}>Liquidity zones</span>
                    <select
                      className={styles.select}
                      value={qualityFilters.liquidity}
                      onChange={(e) => setQualityFilters((prev) => ({ ...prev, liquidity: e.target.value }))}
                    >
                      <option value="ANY">Any</option>
                      <option value="ALIGNED">Aligned only</option>
                    </select>
                  </div>
                  <label className={styles.menuToggle}>
                    <input
                      type="checkbox"
                      checked={qualityFilters.requireVWAP}
                      onChange={() =>
                        setQualityFilters((prev) => ({ ...prev, requireVWAP: !prev.requireVWAP }))
                      }
                    />
                    {' '}Require VWAP
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={`${styles.panel} ${styles.controls}`}>
        <MacroRegimeBadge snapshot={macroSnapshot} stale={macroStale} />
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Asset</span>
          <select className={styles.select} value={symbol} onChange={(e) => setSymbol(e.target.value)}>
            {symbolOptions.map((pair) => (
              <option key={pair} value={pair}>
                {pair}
              </option>
            ))}
          </select>
          <div className={styles.symbolAddRow}>
            <input
              className={styles.input}
              type="text"
              value={newSymbol}
              placeholder="Add equity ticker (e.g., AAPL)"
              onChange={(e) => {
                setSymbolError(null);
                setNewSymbol(e.target.value);
              }}
            />
            <button
              type="button"
              className={styles.addButton}
              aria-label="Add equity ticker"
              onClick={() => {
                const raw = newSymbol.trim().toUpperCase();
                if (!raw) {
                  setSymbolError('Enter a symbol.');
                  return;
                }
                const cleaned = raw.replace(/\\s+/g, '');
                const normalized =
                  cleaned.includes('/') || cleaned.length !== 6
                    ? cleaned
                    : cleaned.slice(0, 3) + '/' + cleaned.slice(3);
                if (!/^[A-Z0-9/]+$/.test(normalized)) {
                  setSymbolError('Symbols must be alphanumeric.');
                  return;
                }
                setCustomSymbols((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
                setSymbol(normalized);
                setNewSymbol('');
              }}
            >
              +
            </button>
          </div>
          {symbolError && <div className={styles.symbolError}>{symbolError}</div>}
          {customSymbols.length > 0 && (
            <div className={styles.symbolList}>
              {customSymbols.map((custom) => (
                <span key={custom} className={styles.symbolChip}>
                  {custom}
                  <button
                    type="button"
                    className={styles.symbolRemove}
                    onClick={() => {
                      setCustomSymbols((prev) => prev.filter((item) => item !== custom));
                      if (symbol === custom) {
                        const fallback = g50Currencies[0]?.pair || 'EUR/USD';
                        setSymbol(fallback);
                      }
                    }}
                    aria-label={`Remove ${custom}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Timeframe</span>
          <select className={styles.select} value={timeframe} onChange={(e) => setTimeframe(e.target.value as Timeframe)}>
            {timeframeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Next refresh</span>
          <div className={styles.countdown}>{refreshCountdown || '--'}</div>
        </div>

        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Current session (GMT)</span>
          <div className={styles.countdown}>
            {sessionLabel} · {sessionTime} GMT
          </div>
        </div>

        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Date range (ET)</span>
          <input
            className={styles.input}
            type="datetime-local"
            value={dateRange.start}
            onChange={(e) => {
              setAutoEnd(false);
              setDateRange((prev) => ({ ...prev, start: e.target.value }));
            }}
          />
          <input
            className={styles.input}
            type="datetime-local"
            value={dateRange.end}
            onChange={(e) => {
              setAutoEnd(false);
              setDateRange((prev) => ({ ...prev, end: e.target.value }));
            }}
          />
        </div>

        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Session</span>
          <select className={styles.select} value={session} onChange={(e) => setSession(e.target.value as SessionKey)}>
            {sessionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {session === 'CUSTOM' && (
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>Custom session (ET)</span>
            <input
              className={styles.input}
              type="time"
              value={customSessionStart}
              onChange={(e) => setCustomSessionStart(e.target.value)}
            />
            <input
              className={styles.input}
              type="time"
              value={customSessionEnd}
              onChange={(e) => setCustomSessionEnd(e.target.value)}
            />
          </div>
        )}

        <div className={styles.toggleRow}>
          <label>
            <input type="checkbox" checked={onlyActive} onChange={() => setOnlyActive((prev) => !prev)} />
            {' '}Show only active
          </label>
        </div>
        <div className={styles.toggleRow}>
          <label>
            <input type="checkbox" checked={strictMode} onChange={() => setStrictMode((prev) => !prev)} />
            {' '}Strict mode
          </label>
        </div>
      </div>

      <div className={`${styles.chartPanel} ${styles.panel}`}>
        {loading && <div>Loading intraday data…</div>}
        {error && <div>{error}</div>}
        {!loading && !error && chartData && (
          <Plot
            data={chartData.traces as any}
            layout={{
              title: `${symbol} Intraday (${timeframe})`,
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              font: { color: axisColor },
              xaxis: {
                title: 'Time (ET)',
                side: 'bottom',
                tickfont: { color: axisColor },
                titlefont: { color: axisColor },
                automargin: true,
                range: chartRangeOverride
                  ? [new Date(chartRangeOverride.start), new Date(chartRangeOverride.end)]
                  : undefined
              },
              yaxis: {
                title: 'Price',
                side: 'right',
                tickfont: { color: axisColor },
                titlefont: { color: axisColor },
                tickformat: priceTickFormat,
                hoverformat: priceTickFormat,
                automargin: true,
                ticklabeloverflow: 'allow',
                range: chartData.yRange || undefined,
                autorange: chartData.yRange ? false : true
              },
              shapes: chartData.shapes,
              annotations: chartData.annotations,
              margin: { t: 40, r: 70, b: 50, l: 40 },
              legend: { orientation: 'h', font: { color: axisColor } }
            }}
            config={{ responsive: true }}
            style={{ width: '100%', height: '70vh' }}
          />
        )}
      </div>

      <div className={`${styles.panel}`}>
        <div className={styles.setupsHeader}>
          <div className={styles.setupsHeaderLeft}>
            <strong className={styles.setupsHeaderTitle}>Detected Setups</strong>
            <span className={styles.educationIcon} tabIndex={0} aria-label="Setup coverage">
              i
              <span className={styles.educationTooltip}>
                <span className={styles.educationTooltipTitle}>Setups scanned</span>
                <ul className={styles.educationTooltipList}>
                  <li>EMA Pullback</li>
                  <li>Flag / Pennant</li>
                  <li>VWAP Reclaim</li>
                  <li>Range High/Low Fade</li>
                  <li>Failed Breakout (Trap)</li>
                  <li>Opening Range Breakout (ORB)</li>
                  <li>Momentum Reversal (Exhaustion)</li>
                  <li>FVG Execution Layer (refinement)</li>
                </ul>
              </span>
            </span>
          </div>
        </div>
        <div className={styles.setupsTabs}>
          <button
            type="button"
            className={`${styles.setupsTab} ${setupsTab === 'current' ? styles.setupsTabActive : ''}`}
            onClick={() => setSetupsTab('current')}
          >
            Current ({filteredSetups.length})
          </button>
          <button
            type="button"
            className={`${styles.setupsTab} ${setupsTab === 'history' ? styles.setupsTabActive : ''}`}
            onClick={() => setSetupsTab('history')}
          >
            History ({historyItems.length})
          </button>
          {showLiquidityZones && (
            <button
              type="button"
              className={`${styles.setupsTab} ${setupsTab === 'liquidity' ? styles.setupsTabActive : ''}`}
              onClick={() => setSetupsTab('liquidity')}
            >
              Liquidity Zones ({liquidityZones.length})
            </button>
          )}
          {setupsTab === 'history' && (
            <button
              type="button"
              className={styles.historyStatsButton}
              onClick={() => setShowHistoryStats(true)}
              aria-label="History success and failure rates"
            >
              <BarChart2 size={16} />
              <span className={styles.historyStatsTooltip}>Success vs Failed stats</span>
            </button>
          )}
        </div>
        {showHistoryStats && (
          <div className={styles.historyStatsOverlay} role="dialog" aria-modal="true">
            <div className={styles.historyStatsBackdrop} onClick={() => setShowHistoryStats(false)} />
            <div className={`${styles.historyStatsModal} ${styles.historyStatsCard}`}>
              <div className={styles.historyStatsHeader}>
                <div className={styles.historyStatsTitle}>History Outcomes</div>
                <button
                  type="button"
                  className={styles.historyStatsClose}
                  onClick={() => setShowHistoryStats(false)}
                >
                  ×
                </button>
              </div>
              <div className={styles.historyStatsBody}>
                {historyStats.total ? (
                  <>
                    <div className={styles.historyStatsViz}>
                      <div className={styles.historyStatsPieChart}>
                        <Plot
                          data={historyPieData}
                          layout={historyPieLayout}
                          config={{ displayModeBar: false, responsive: true }}
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                      <div className={styles.historyStatsLegend}>
                        <div className={styles.historyStatsLegendItem}>
                          <span className={`${styles.historyStatsLegendSwatch} ${styles.historyStatsLegendSuccess}`} />
                          Success
                        </div>
                        <div className={styles.historyStatsLegendItem}>
                          <span className={`${styles.historyStatsLegendSwatch} ${styles.historyStatsLegendFailed}`} />
                          Failed
                        </div>
                        <div className={styles.historyStatsLegendItem}>
                          <span className={`${styles.historyStatsLegendSwatch} ${styles.historyStatsLegendOther}`} />
                          Other
                        </div>
                      </div>
                    </div>
                    <div className={styles.historyStatsRow}>
                      <span>Success</span>
                      <span>{historyStats.successPct}%</span>
                    </div>
                    <div className={styles.historyStatsRow}>
                      <span>Failed</span>
                      <span>{historyStats.failedPct}%</span>
                    </div>
                    <div className={styles.historyStatsRow}>
                      <span>Expired</span>
                      <span>{historyStats.expiredPct}%</span>
                    </div>
                    <div className={styles.historyStatsRow}>
                      <span>Both hit</span>
                      <span>{historyStats.mixedPct}%</span>
                    </div>
                    <div className={styles.historyStatsFooter}>
                      Total setups: {historyStats.total}
                    </div>
                    <div className={styles.historyStatsFooter}>
                      Both hit = target and stop reached in the same candle; order is unknown with OHLC data.
                    </div>
                  </>
                ) : (
                  <div className={styles.historyStatsEmpty}>No history data yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
        {setupsTab === 'current' && (
          <>
            {filteredSetups.map(({ setup, display }) => {
              const timingLine =
                display.status === 'TRIGGERED'
                  ? `Triggered: ${display.barsAgo} bars ago`
                  : display.status === 'EXPIRED'
                    ? `Expired: ${display.barsAgo} bars ago`
                    : 'Trigger: pending';
              const patternStartAt = getPatternStartAt(setup);
              const sessionInfo = getSessionInfoForTimestamp(patternStartAt, session);
              const confidenceBullets = getConfidenceBullets(setup);
              const confluence = computeConfluenceBadge(
                setup.direction,
                macroSnapshot?.macroBias ?? null,
                Boolean(macroSnapshot)
              );
              const liquidityBadge = getLiquidityBadge(setup, liquidityZones);
              const rrDisplay = (() => {
                const entry = setup.entry?.level;
                const stop = setup.stop?.level;
                const target = setup.targets?.[0]?.level;
                if (entry == null || stop == null || target == null) return null;
                const round = (value: number) => Number(value.toFixed(priceDecimals));
                const entryRounded = round(entry);
                const stopRounded = round(stop);
                const targetRounded = round(target);
                const risk = Math.abs(entryRounded - stopRounded);
                const reward = Math.abs(targetRounded - entryRounded);
                if (!risk) return null;
                return reward / risk;
              })();
              const checklistDisplay = setup.checklist.map((item) => {
                if (item.key !== 'rr' || rrDisplay == null) return item;
                return { ...item, pass: rrDisplay >= 2 };
              });
              const isFitActive = fitSetupId === setup.id;
              const isTimeZoomActive = timeZoomSetupId === setup.id;
                const handleFitToSetup = () => {
                  if (isFitActive) {
                    setFitSetupId(null);
                    setChartYOverride(null);
                    setFitEnabled(false);
                    return;
                  }
                  const range = getFitRangeForSetup(setup);
                  if (!range) return;
                  setChartYOverride(range);
                  setFitSetupId(setup.id);
                  setFitEnabled(true);
                };
              const handleTimeZoom = () => {
                if (isTimeZoomActive) {
                  setChartRangeOverride(null);
                  setTimeZoomSetupId(null);
                  return;
                }
                const minutes = getTimeframeMinutes(timeframe);
                const bufferMs = minutes * 60 * 1000 * 20;
                const startAt = setup.patternStartAt ?? setup.detectedAt;
                const start = Math.max(startAt - bufferMs, 0);
                const end = setup.detectedAt + bufferMs;
                setFitEnabled(false);
                setFitSetupId(null);
                setChartYOverride(null);
                setChartRangeOverride({ start, end });
                setTimeZoomSetupId(setup.id);
              };
              return (
                <div
                  key={setup.id}
                  className={`${styles.setupCard} ${display.status === 'EXPIRED' ? styles.setupCardExpired : ''}`}
                >
                  <div className={styles.setupTitleRow}>
                    {setupLabel(setup)} {setup.direction}
                    <span className={styles.setupInfoIcon} tabIndex={0} aria-label="Setup details">
                      ?
                      <span className={styles.setupTooltip}>
                        <span className={styles.setupTooltipTitle}>{getSetupTooltip(setup).title}</span>
                        <ul className={styles.setupTooltipList}>
                          {getSetupTooltip(setup).bullets.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      </span>
                    </span>
                  </div>
                  <div className={styles.setupBadgeRow}>
                    <span className={`${styles.badge} ${statusBadgeClass(display.status)}`}>{display.status}</span>
                    <span className={`${styles.badge} ${confidenceBadgeClass(setup.confidence)} ${styles.confidenceBadge}`}>
                      {setup.confidence}
                      <span className={styles.confidenceTooltip}>
                        <span className={styles.confidenceTooltipTitle}>Confidence drivers</span>
                        <ul className={styles.confidenceTooltipList}>
                          {confidenceBullets.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      </span>
                    </span>
                  </div>
                  <div className={styles.setupBadgeRow}>
                    {setup.execution?.model === 'FVG Refined' && (
                      <span className={`${styles.badge} ${styles.badgeFvg}`}>FVG Refined</span>
                    )}
                    <span
                      className={`${styles.badge} ${styles.liquidityBadge} ${styles[`liquidity${liquidityBadge.tone}`]}`}
                    >
                      {liquidityBadge.label}
                      <span className={styles.liquidityTooltip}>{liquidityBadge.tooltip}</span>
                    </span>
                    <ConfluenceBadge confluence={confluence} />
                  </div>
                  <div className={styles.sessionBadge}>
                    Session: {sessionInfo.label}
                    {sessionInfo.liquidity ? ` · ${sessionInfo.liquidity}` : ''}
                  </div>
                  <div className={styles.levels}>
                    Entry: {setup.entry.level.toFixed(priceDecimals)} · Stop: {setup.stop.level.toFixed(priceDecimals)}
                  </div>
                  {setup.execution?.model === 'FVG Refined' && setup.entryOriginal && setup.stopOriginal && (
                    <div className={styles.levels}>
                      Entry (base): {setup.entryOriginal.level.toFixed(priceDecimals)} · Stop (base):{' '}
                      {setup.stopOriginal.level.toFixed(priceDecimals)}
                    </div>
                  )}
                  <div className={styles.levels}>
                    Targets: {setup.targets.map((t) => t.level.toFixed(priceDecimals)).join(' / ')}
                  </div>
                  <div className={styles.levels}>{timingLine}</div>
                  {display.status === 'EXPIRED' && (
                    <div className={styles.expiredNote}>Expired — conditions no longer valid</div>
                  )}
                  <div className={styles.setupActions}>
                    <label className={styles.zoneToggle}>
                      <input
                        type="checkbox"
                        checked={visibleZoneIds[setup.id] !== false}
                        onChange={() =>
                          setVisibleZoneIds((prev) => ({
                            ...prev,
                            [setup.id]: !(prev[setup.id] !== false)
                          }))
                        }
                      />
                      Show zone
                    </label>
                    <button type="button" className={styles.fitSetupButton} onClick={handleFitToSetup}>
                      {isFitActive ? 'Reset fit' : 'Fit to setup'}
                    </button>
                    <button type="button" className={styles.zoomSetupButton} onClick={handleTimeZoom}>
                      {isTimeZoomActive ? 'Reset zoom' : 'Zoom time'}
                    </button>
                  </div>
                  <ul className={styles.list}>
                    {setup.why.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <div className={styles.checklist}>
                    {checklistDisplay.map((item) => (
                      <span
                        key={item.key}
                        className={`${styles.checkItem} ${item.pass ? styles.checkPass : styles.checkFail}`}
                      >
                        {item.label}
                      </span>
                    ))}
                  </div>
                  <div className={styles.levels}>Detected: {formatEt(patternStartAt)} ET</div>
                </div>
              );
            })}
          </>
        )}
        {setupsTab === 'liquidity' && (
          <div className={styles.liquidityList}>
            {liquidityZones.length ? (
              liquidityZones.map((zone, index) => {
                const low = Math.min(zone.proximal, zone.distal);
                const high = Math.max(zone.proximal, zone.distal);
                const zoneId = getZoneId(zone);
                const rangeMs = getZoneRangeMs(zone);
                const bufferMs = 15 * 60 * 1000;
                const isZoomed =
                  chartRangeOverride &&
                  Math.abs(chartRangeOverride.start - (rangeMs.start - bufferMs)) < bufferMs &&
                  Math.abs(chartRangeOverride.end - (rangeMs.end + bufferMs)) < bufferMs;
                return (
                  <div key={`${zone.startTime}-${zone.endTime}-${index}`} className={styles.liquidityCard}>
                    <div className={styles.liquidityHeader}>
                      <span className={styles.liquidityTitle}>
                        {(zone.type ?? '').toLowerCase() === 'demand' ? 'Demand' : 'Supply'} Zone
                      </span>
                      <span className={styles.liquidityRange}>
                        {low.toFixed(priceDecimals)} – {high.toFixed(priceDecimals)}
                      </span>
                    </div>
                    <div className={styles.liquidityActions}>
                      <label className={styles.liquidityToggle}>
                        <input
                          type="checkbox"
                          checked={Boolean(liquidityZoneVisibility[zoneId])}
                          onChange={() => {
                            setLiquidityZoneVisibility((prev) => {
                              const next = {
                                ...prev,
                                [zoneId]: !prev[zoneId]
                              };
                              const enabled = next[zoneId];
                              if (enabled) {
                                const zoneStart = moment.tz(zone.startTime, 'YYYY-MM-DD HH:mm:ss', 'UTC');
                                const zoneEnd = moment.tz(zone.endTime, 'YYYY-MM-DD HH:mm:ss', 'UTC');
                                const rangeStart = moment.tz(dateRange.start, TIME_ZONE);
                                const rangeEnd = moment.tz(dateRange.end, TIME_ZONE);
                                if (zoneStart.isBefore(rangeStart) || zoneEnd.isAfter(rangeEnd)) {
                                  setAutoEnd(false);
                                  const start = zoneStart.clone().subtract(15, 'minutes');
                                  const end = zoneEnd.clone().add(15, 'minutes');
                                  setDateRange({
                                    start: start.tz(TIME_ZONE).format('YYYY-MM-DDTHH:mm'),
                                    end: end.tz(TIME_ZONE).format('YYYY-MM-DDTHH:mm')
                                  });
                                }
                              }
                              return next;
                            });
                          }}
                        />
                        {' '}View on chart
                      </label>
                      <button
                        type="button"
                        className={styles.liquidityZoomButton}
                        onClick={() => {
                          if (isZoomed) {
                            setChartRangeOverride(null);
                          } else {
                            setChartRangeOverride({
                              start: rangeMs.start - bufferMs,
                              end: rangeMs.end + bufferMs
                            });
                            setFitEnabled(false);
                            setFitSetupId(null);
                            setChartYOverride(null);
                          }
                        }}
                      >
                        {isZoomed ? 'Reset zoom' : 'Zoom in'}
                      </button>
                    </div>
                    <div className={styles.liquidityMeta}>
                      <span>Start: {formatZoneTime(zone.startTime)} ET</span>
                      <span>End: {formatZoneTime(zone.endTime)} ET</span>
                    </div>
                    <div className={styles.liquidityMeta}>
                      <span>Width: {zone.width.toFixed(priceDecimals)}</span>
                      <span>Persistence: {zone.persistence}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={styles.liquidityEmpty}>No liquidity zones for this range.</div>
            )}
          </div>
        )}
        {setupsTab === 'history' && (
          <>
            {historyItems.length ? (
              <div className={styles.historyList}>
                {pagedHistoryItems.map((item) => {
                  const patternStartAt = getPatternStartAt(item.setup);
                  const isExpanded = Boolean(expandedHistoryIds[item.id]);
                  const statusLabel =
                    item.outcome === 'SUCCESS'
                      ? 'COMPLETED'
                      : item.outcome === 'FAILED'
                        ? 'FAILED'
                        : 'TRIGGERED';
                  const statusClass =
                    item.outcome === 'SUCCESS'
                      ? styles.historyStatusSuccess
                      : item.outcome === 'FAILED'
                        ? styles.historyStatusFailed
                        : styles.historyStatusTriggered;
                  const detectedTime = formatEt(patternStartAt);
                  const rrValue = (() => {
                    const entry = item.setup.entry?.level;
                    const stop = item.setup.stop?.level;
                    const target = item.setup.targets?.[0]?.level;
                    if (entry == null || stop == null || target == null) return null;
                    const risk = Math.abs(entry - stop);
                    const reward = Math.abs(target - entry);
                    if (!risk) return null;
                    return reward / risk;
                  })();
                  const outcomeReason = (() => {
                    if (item.outcome === 'SUCCESS') {
                      const target = item.setup.targets?.[0]?.level;
                      return target != null
                        ? `Hit target ${target.toFixed(priceDecimals)}`
                        : 'Target reached';
                    }
                    if (item.outcome === 'FAILED') {
                      const stop = item.setup.stop?.level;
                      return stop != null
                        ? `Stopped at ${stop.toFixed(priceDecimals)}`
                        : 'Stop hit';
                    }
                    if (item.outcome === 'MIXED') {
                      return 'Target and stop hit in the same candle (order unknown)';
                    }
                    if (item.outcome === 'EXPIRED') {
                      return 'Expired before trigger';
                    }
                    return 'Detected automatically from intraday structure';
                  })();
                  return (
                    <div key={item.id} className={styles.historyCard}>
                      <div className={styles.historyCardHeader}>
                        <button
                          type="button"
                          className={styles.historyExpandButton}
                          onClick={() =>
                            setExpandedHistoryIds((prev) => ({
                              ...prev,
                              [item.id]: !prev[item.id]
                            }))
                          }
                          aria-label={isExpanded ? 'Collapse history item' : 'Expand history item'}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>
                        <div className={styles.historyCardTitle}>
                          {setupLabel(item.setup)} {item.setup.direction}
                        </div>
                        <span className={`${styles.historyStatusBadge} ${statusClass}`}>{statusLabel}</span>
                      </div>
                      {isExpanded && (
                        <>
                          <div className={styles.historyTimestamp}>{detectedTime}</div>
                          <div className={styles.historyGrid}>
                            <div className={styles.historyGridItem}>
                              <span className={styles.historyGridLabel}>Session</span>
                              <span className={styles.historyGridValue}>
                                {getSessionInfoForTimestamp(patternStartAt, session).label}
                              </span>
                            </div>
                            <div className={styles.historyGridItem}>
                              <span className={styles.historyGridLabel}>Entry</span>
                              <span className={styles.historyGridValue}>
                                {item.setup.entry.level.toFixed(priceDecimals)}
                              </span>
                            </div>
                            <div className={styles.historyGridItem}>
                              <span className={styles.historyGridLabel}>Stop</span>
                              <span className={styles.historyGridValue}>
                                {item.setup.stop.level.toFixed(priceDecimals)}
                              </span>
                            </div>
                            <div className={styles.historyGridItem}>
                              <span className={styles.historyGridLabel}>Targets</span>
                              <span className={styles.historyGridValue}>
                                {item.setup.targets.map((t) => t.level.toFixed(priceDecimals)).join(' / ')}
                              </span>
                            </div>
                            <div className={styles.historyGridItem}>
                              <span className={styles.historyGridLabel}>R:R</span>
                              <span className={styles.historyGridValue}>
                                {rrValue != null ? rrValue.toFixed(2) : '—'}
                              </span>
                            </div>
                          </div>
                          <div className={styles.historyFooter}>
                            <span className={styles.historyFooterNote}>{outcomeReason}</span>
                            <label className={styles.historyToggle}>
                              <input
                                type="checkbox"
                                checked={Boolean(historyVisibleIds[item.id])}
                                onChange={() =>
                                  setHistoryVisibleIds((prev) => ({
                                    ...prev,
                                    [item.id]: !prev[item.id]
                                  }))
                                }
                              />
                              Show on chart
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.historyEmpty}>No completed setups yet.</div>
            )}
            {historyItems.length > historyPageSize && (
              <div className={styles.historyPagination}>
                <button
                  type="button"
                  className={styles.historyPageButton}
                  onClick={() => setHistoryPage((prev) => Math.max(1, prev - 1))}
                  disabled={historyPage === 1}
                >
                  Prev
                </button>
                <span className={styles.historyPageInfo}>
                  Page {historyPage} of {totalHistoryPages}
                </span>
                <button
                  type="button"
                  className={styles.historyPageButton}
                  onClick={() => setHistoryPage((prev) => Math.min(totalHistoryPages, prev + 1))}
                  disabled={historyPage === totalHistoryPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
        <div className={styles.macroFootnote}>
          CFTC regime is weekly. Intraday setups can trigger inside any regime.
        </div>
      </div>
    </div>
  );
}
